import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/ReporteEstimacionAlumnadoStyles/ReporteEstimacionAlumnado.module.css';
import { getCycles } from '../../api/ciclosEscolares';
import { getSeccionesAcademicas } from '../../api/secciones_academicas';
import { getPrograms } from '../../api/programs';

/**
 * Filtros del Reporte Estimación de Alumnado.
 *
 * Props:
 *  - filters: { id_ciclo_escolar, id_seccion, id_programa }
 *  - onChange(key, value)
 *  - onGenerate()
 *  - loading: boolean
 *  - onContextResolved({ ciclo, seccion, programa })
 */
function FiltrosReporte({ filters, onChange, onGenerate, loading, onContextResolved }) {
  const [ciclos, setCiclos] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [programas, setProgramas] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      getCycles().catch(() => []),
      getSeccionesAcademicas().catch(() => []),
      getPrograms().catch(() => []),
    ]).then(([cic, sec, prog]) => {
      if (!mounted) return;
      setCiclos(Array.isArray(cic) ? cic : []);
      setSecciones(Array.isArray(sec) ? sec : []);
      setProgramas(Array.isArray(prog) ? prog : []);
    });
    return () => { mounted = false; };
  }, []);

  // Programas filtrados por sección seleccionada
  const programasFiltrados = useMemo(() => {
    let list = programas;
    if (filters.id_seccion) {
      list = list.filter((p) => {
        if (!p.sectionId) return true;
        return String(p.sectionId) === String(filters.id_seccion);
      });
    }
    return list;
  }, [programas, filters.id_seccion]);

  // Limpiar programa si ya no está en la lista filtrada
  useEffect(() => {
    if (!filters.id_programa) return;
    const stillPresent = programasFiltrados.some((p) => String(p.id) === String(filters.id_programa));
    if (!stillPresent) onChange('id_programa', '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programasFiltrados]);

  const resolveContext = () => {
    const ciclo = ciclos.find((c) => String(c.id) === String(filters.id_ciclo_escolar));
    const seccion = secciones.find((s) => String(s.id) === String(filters.id_seccion));
    const programa = programas.find((p) => String(p.id) === String(filters.id_programa));
    return {
      ciclo: ciclo ? (ciclo.name || ciclo.nombre_ciclo) : '—',
      seccion: seccion ? seccion.nombre : 'Todas',
      programa: programa ? programa.name : 'Todos',
    };
  };

  const handleGenerate = () => {
    if (typeof onContextResolved === 'function') {
      onContextResolved(resolveContext());
    }
    onGenerate();
  };

  const disabled = loading || !filters.id_ciclo_escolar;

  return (
    <section className={styles.filtersSection}>
      <div className={styles.filtersRow}>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Sección</label>
          <select
            className={styles.filterSelect}
            value={filters.id_seccion || ''}
            onChange={(e) => onChange('id_seccion', e.target.value)}
          >
            <option value="">Todas</option>
            {secciones.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Programa</label>
          <select
            className={styles.filterSelect}
            value={filters.id_programa || ''}
            onChange={(e) => onChange('id_programa', e.target.value)}
          >
            <option value="">Todos</option>
            {programasFiltrados.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Ciclo Escolar *</label>
          <select
            className={styles.filterSelect}
            value={filters.id_ciclo_escolar || ''}
            onChange={(e) => onChange('id_ciclo_escolar', e.target.value)}
          >
            <option value="">Selecciona un ciclo</option>
            {ciclos.map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.nombre_ciclo}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup} style={{ flex: '0 0 auto', minWidth: 'auto' }}>
          <label className={styles.filterLabel}>&nbsp;</label>
          <button
            type="button"
            className={styles.generateButton}
            onClick={handleGenerate}
            disabled={disabled}
          >
            Generar Reporte
          </button>
        </div>

      </div>
    </section>
  );
}

export default FiltrosReporte;
